import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CrosshairIcon, PlusIcon } from '@phosphor-icons/react';
import { PageContent } from '@/components/page-content';
import { SectionHeader } from '@/components/section-header';
import { Button } from '@/components/ui/button';
import { RequireOrgEdit } from '@/components/require-permission';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { useFeatures } from '@/hooks/use-features';
import { companyProfilesOptions } from '@/hooks/query-options';
import type { CompanyProfile } from '@/api/company-profiles-service';
import { TargetAccountCard } from '@/components/profiles/company-profile-cards';
import { AddTargetAccountDialog } from '@/components/profiles/add-target-account-dialog';

// "Target Accounts" tab: the living account briefs on the accounts you sell
// into (`kind === 'account'`). Created by Add Target Account, promoting a
// company from Companies, or saving a Prospect Research brief.
export const Route = createFileRoute(
  '/_auth/_side-layout/actions/profiles/personas',
)({
  component: TargetAccountsPage,
});

function TargetAccountsPage() {
  const { isEnabled } = useFeatures();
  const { data, isLoading } = useQuery(companyProfilesOptions());
  const navigate = useNavigate();
  const accounts = (data?.profiles ?? []).filter((p) => p.kind === 'account');

  const openProfile = (profile: CompanyProfile) =>
    navigate({
      to: '/actions/profiles/$profileId',
      params: { profileId: profile.id },
    });

  return (
    <PageContent>
      <div className="space-y-4">
        <SectionHeader
          title="Target Accounts"
          subTitle="Living account briefs on the accounts you're selling into: research, prioritization, and signals in one place."
        />

        {!isEnabled('targetAccounts') ? (
          <ComingSoonState />
        ) : isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : accounts.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {accounts.map((profile) => (
              <TargetAccountCard
                key={profile.id}
                profile={profile}
                onView={openProfile}
              />
            ))}
          </div>
        )}
      </div>
    </PageContent>
  );
}

// Repeats the header's Add Target Account, so the page has one first step.
function EmptyState() {
  const [addOpen, setAddOpen] = useState(false);

  return (
    <>
      <Empty className="border">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CrosshairIcon />
          </EmptyMedia>
          <EmptyTitle>No Target Accounts Yet</EmptyTitle>
          <EmptyDescription>
            Add a company you sell into, or promote one from Companies. Each
            account keeps its research, prioritization, and signals in one
            place.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <RequireOrgEdit>
            <Button iconLeft={<PlusIcon />} onClick={() => setAddOpen(true)}>
              Add Target Account
            </Button>
          </RequireOrgEdit>
        </EmptyContent>
      </Empty>
      <AddTargetAccountDialog open={addOpen} onOpenChange={setAddOpen} />
    </>
  );
}

// Shown when the `targetAccounts` flag is off — honest "coming soon" placeholder
// (super-admins enable the org's flag to see the real surface).
function ComingSoonState() {
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <CrosshairIcon />
        </EmptyMedia>
        <EmptyTitle>Target Accounts: Coming Soon</EmptyTitle>
        <EmptyDescription>
          Saved account briefs on the specific accounts you&apos;re selling
          into, where research, qualification, and outreach live. Different from
          Company Profiles, which describe your own company and ICP.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}
